import { type ActionEvent, Controller } from "@hotwired/stimulus"

import TabsController from "@/controllers/tabs_controller"
import { formatDMMM, formatMMMM } from "@/helpers/date_format"

export default class extends Controller {
  static outlets = ["tabs"]
  declare readonly tabsOutlet: TabsController

  static targets = [
    "dateRadio",
    "flexibleDayRadio",
    "flexibleMonthCheckbox",
    "hiddenArrivalDateInput"
  ]

  declare readonly dateRadioTargets: HTMLInputElement[]
  declare readonly flexibleDayRadioTargets: HTMLInputElement[]
  declare readonly flexibleMonthCheckboxTargets: HTMLInputElement[]
  declare readonly hiddenArrivalDateInputTarget: HTMLInputElement

  // Manually track selected radio so we can un-check it on click.
  declare selectedDateRadio?: HTMLInputElement

  connect(): void {
    this.selectedDateRadio = this.dateRadioTargets.find((radio) => radio.checked)
  }

  deselectDate(): void {
    this.selectedDateRadio?.click()
    this.deselectFlexibleDates()
  }

  deselectDefaultFlexibleDay(): void {
    // If no months selected, deselect default flexible day so it's not sent to server.
    if (!this.hasFlexibleMonth) {
      this.deselectWithEvent(this.defaultFlexibleDayRadio)
    }
  }

  deselectIfEmpty({ currentTarget }: ActionEvent): void {
    if (!(currentTarget as HTMLInputElement).value) {
      this.deselectDate()
    }
  }

  get isDateSelected(): boolean {
    // Note: This does not deem "Any" to be a selection.
    return !!this.selectedDateRadio || this.hasFlexibleDate
  }

  selectCalendarTab(): void {
    this.tabsOutlet.activateTabAtIndex(this.hasFlexibleDate ? 1 : 0)
  }

  selectDate({ currentTarget }: ActionEvent): void {
    const dateRadio = currentTarget as HTMLInputElement

    // Un-check radio if already selected
    if (dateRadio === this.selectedDateRadio) {
      dateRadio.checked = false
      this.selectedDateRadio = undefined
      this.hiddenArrivalDateInputTarget.value = ""
    } else {
      this.selectedDateRadio = dateRadio
      this.hiddenArrivalDateInputTarget.value = dateRadio.value
      this.deselectFlexibleDates()
    }
  }

  selectFlexibleDay(): void {
    this.selectedDateRadio?.click()
  }

  selectFlexibleMonth({ currentTarget }: ActionEvent): void {
    this.selectedDateRadio?.click()

    const isSelected = (currentTarget as HTMLInputElement).checked
    const { defaultFlexibleDayRadio } = this

    // If at least one month selected, a day needs to be selected as well. Otherwise if no months
    // selected, deselect default day if necessary.
    if (isSelected && !this.hasFlexibleDay) {
      defaultFlexibleDayRadio.click()
    } else if (!this.hasFlexibleMonth) {
      this.deselectWithEvent(defaultFlexibleDayRadio)
    }
  }

  get summaryText(): string {
    return this.hasFlexibleDate ? this.flexibleDateSummaryText : this.dateSummaryText
  }

  private get dateSummaryText(): string {
    const { selectedDateRadio } = this
    return selectedDateRadio ? formatDMMM(new Date(selectedDateRadio.value)) : "Any"
  }

  private get defaultFlexibleDayRadio(): HTMLInputElement {
    return this.flexibleDayRadioTargets.at(0)!
  }

  private deselectFlexibleDates(): void {
    this.flexibleDayRadioTargets.forEach((radio) => {
      this.deselectWithEvent(radio)
    })

    this.flexibleMonthCheckboxTargets.forEach((checkbox) => {
      if (checkbox.checked) {
        checkbox.click()
      }
    })
  }

  private deselectWithEvent(input: HTMLInputElement): void {
    if (input.checked) {
      input.checked = false
      input.dispatchEvent(new Event("change"))
    }
  }

  private get flexibleDateSummaryText(): string {
    const flexibleDayRadio = this.flexibleDayRadioTargets.find((radio) => radio.checked)
    const flexibleMonths = this.flexibleMonthCheckboxTargets
      .flatMap((checkbox) => (checkbox.checked ? [formatMMMM(new Date(checkbox.value))] : []))
      .join(", ")
    const anyFlexibleMonths = flexibleMonths.length > 0

    if (!flexibleDayRadio || flexibleDayRadio === this.defaultFlexibleDayRadio) {
      return `Any${anyFlexibleMonths ? ` day in ${flexibleMonths}` : ""}`
    } else {
      return `Any ${flexibleDayRadio.value}${anyFlexibleMonths ? ` in ${flexibleMonths}` : ""}`
    }
  }

  private get hasFlexibleDate(): boolean {
    return this.hasFlexibleDay || this.hasFlexibleMonth
  }

  private get hasFlexibleDay(): boolean {
    // Note the extra condition here -- the default flexible day doesn't "count" as a selected flexible day.
    return this.flexibleDayRadioTargets.some(
      (radio) => radio.checked && radio !== this.defaultFlexibleDayRadio
    )
  }

  private get hasFlexibleMonth(): boolean {
    return this.flexibleMonthCheckboxTargets.some((checkbox) => checkbox.checked)
  }
}
